import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'config/i18n';
// Material UI Core
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useHandleResponse from 'utils/useHandleResponse';
import axios, { API } from 'config/axios';
import {
  Typography,
  TextField,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Dialog,
  MenuItem,
  Button,
} from '@material-ui/core';
import {
  useLayoutState,
  useLayoutSnackbar,
  useLayoutLoadingSpinner,
} from 'components/shared/Layout';
import FormErrors from 'components/shared/FormErrors';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Material UI icons
import CloseIcon from '@material-ui/icons/Close';

import { usRegionsWithCodes } from 'components/ownerPages/earningsAndPayouts/payoutMethodSettings/usRegionsWithCodes';
import { accountsEligibleCountriesWithCurrencies } from 'components/ownerPages/earningsAndPayouts/payoutMethodSettings/accountsEligibleCountriesWithCurrencies';
import { acceptableAccountHolderCountries } from 'components/ownerPages/earningsAndPayouts/payoutMethodSettings/acceptableAccountHolderCountries';

// ISO 3166-1 alpha-2
// ⚠️ No support for IE 11
function countryToFlag(isoCode) {
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode
      .toUpperCase()
      .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    : isoCode;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    width: theme.typography.pxToRem(1200),
  },
  dialogContent: {
    margin: theme.spacing(2, 0),
  },
  right: {
    textAlign: 'right',
  },
  submit: {
    marginTop: theme.spacing(2),
    color: theme.palette.grey[300],
    '&  a': {
      color: theme.palette.primary.main,
    },
  },

  option: {
    fontSize: 15,
    '& > span': {
      marginRight: 10,
      fontSize: 18,
    },
  },
}));

const AddPayoutMethodDialog = (props) => {
  const classes = useStyles();
  const {
    onClose, open, t, ...other
  } = props;

  const [snackbar, setSnackbar] = useLayoutSnackbar();

  const [loading, setLoading] = useLayoutLoadingSpinner();

  const [errorMsgs, setErrorMsgs] = useState(null);

  const [value, setValue] = useState({}); // All form fields

  const { layoutState } = useLayoutState();

  const { handleError } = useHandleResponse();
  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const handleClose = () => {
    onClose();
  };

  const handleOnChange = (e) => {
    setValue({
      ...value,
      [e.target.name]: e.target.value,
    });
  };

  const cancelToken = axios.CancelToken;
  const source = cancelToken.source();

  const submit = () => {
    setErrorMsgs(null);
    setLoading(true);
    // 'value.country_code' is object like { code: "US", label: "United States", phone: "1"}
    // so we'll only send 'code' property
    const payload = {
      ...value,
      country_code: value.country_code ? value.country_code.code : '',
      state_code: value.state_code ? value.state_code.code : '',
      holder_country_code: value.holder_country_code ? value.holder_country_code.code : '',
      authenticity_token: layoutState.authenticity_token,
    };
    axios
      .post(API.owner.payoutTransferMethods.bankAccounts, payload, {
        cancelToken: source.token,
      })
      .then(() => {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: t(
            'fe_earnings_and_payouts_page:add_payout_method_dialog.payout_method_added_successfully',
          ),
        });
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        try {
          switch (error.response.status) {
            case 406:
              setErrorMsgs(error.response.data);
              break;
            default:
              handleError(error);
          }
        } catch (e) {
          handleError(error);
        }
      });
    return () => {
      source.cancel('Operation canceled by the user.');
    };
  };

  return (
    <Dialog
      id="add-payout-method-dialog"
      open={open}
      classes={{
        paper: classes.paper,
      }}
      fullScreen={matches}
      maxWidth="lg"
      keepMounted
      onClose={handleClose}
      {...other}
    >
      <DialogTitle>
        <Typography variant="h5">
          {t('fe_earnings_and_payouts_page:add_payout_method_dialog.title')}
        </Typography>
        <IconButton onClick={handleClose} aria-label="Cancel" color="secondary">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {errorMsgs && <FormErrors errorMsgs={errorMsgs} showBaseErrors />}
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <Autocomplete
              id="country-select"
              options={accountsEligibleCountriesWithCurrencies}
              classes={{
                option: classes.option,
              }}
              autoHighlight
              getOptionLabel={(option) => option.name || ''}
              renderOption={(option) => (
                <>
                  <span>{countryToFlag(option.code)}</span>
                  {option.name}
                </>
              )}
              value={value.country_code || null}
              onChange={(event, newValue) => {
                setValue({
                  ...value,
                  country_code: newValue || null,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t(
                    'fe_earnings_and_payouts_page:add_payout_method_dialog.fields.country_code',
                  )}
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                  }}
                  error={Boolean(errorMsgs?.errors && errorMsgs.errors.country_code)}
                />
              )}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="country_code" />
          </Grid>
          {value.country_code && (
            <Grid item md={6} xs={12}>
              <Autocomplete
                id="currency-select"
                options={
                  accountsEligibleCountriesWithCurrencies.find(
                    (c) => c.code == value.country_code.code,
                  ).currencies_codes
                }
                classes={{
                  option: classes.option,
                }}
                autoHighlight
                getOptionLabel={(option) => option || ''}
                renderOption={(option) => <>{option}</>}
                value={value.currency_code || null}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    currency_code: newValue || null,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(
                      'fe_earnings_and_payouts_page:add_payout_method_dialog.fields.currency_code',
                    )}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                    error={Boolean(errorMsgs?.errors && errorMsgs.errors.currency_code)}
                  />
                )}
              />
              <FormErrors errorMsgs={errorMsgs} errorField="country_code" />
            </Grid>
          )}
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="account_id"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.account_id')}
              variant="outlined"
              name="account_id"
              type="text"
              value={value.account_id || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.account_id)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="account_id" />
          </Grid>
          {value.country_code && value.country_code.code !== 'US' && (
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                id="bank_id"
                required
                color="secondary"
                label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.bank_id')}
                variant="outlined"
                name="bank_id"
                type="text"
                value={value.bank_id || ''}
                onChange={handleOnChange}
                error={Boolean(errorMsgs?.errors && errorMsgs.errors.bank_id)}
              />
              <FormErrors errorMsgs={errorMsgs} errorField="bank_id" />
            </Grid>
          )}
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="branch_id"
              required={value.country_code && value.country_code.code === 'US'}
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.branch_id')}
              variant="outlined"
              name="branch_id"
              type="text"
              value={value.branch_id || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.branch_id)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="branch_id" />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              select
              fullWidth
              id="purpose"
              required
              color="secondary"
              label={t(
                'fe_earnings_and_payouts_page:add_payout_method_dialog.fields.purpose.label',
              )}
              variant="outlined"
              name="purpose"
              type="text"
              value={value.purpose || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.purpose)}
            >
              <MenuItem value="checking">
                {t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.purpose.checking')}
              </MenuItem>
              <MenuItem value="savings">
                {t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.purpose.savings')}
              </MenuItem>
            </TextField>
            <FormErrors errorMsgs={errorMsgs} errorField="purpose" />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="first_name"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.first_name')}
              variant="outlined"
              name="first_name"
              type="text"
              value={value.first_name || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.first_name)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="first_name" />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="last_name"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.last_name')}
              variant="outlined"
              name="last_name"
              type="text"
              value={value.last_name || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.last_name)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="last_name" />
          </Grid>
          <Grid item md={6} xs={12}>
            <Autocomplete
              id="holder-country-select"
              required
              options={acceptableAccountHolderCountries}
              classes={{
                option: classes.option,
              }}
              autoHighlight
              getOptionLabel={(option) => option.name || ''}
              renderOption={(option) => (
                <>
                  <span>{countryToFlag(option.code)}</span>
                  {option.name}
                </>
              )}
              value={value.holder_country_code || null}
              onChange={(event, newValue) => {
                setValue({
                  ...value,
                  holder_country_code: newValue || null,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t(
                    'fe_earnings_and_payouts_page:add_payout_method_dialog.fields.holder_country_code',
                  )}
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                  }}
                  error={Boolean(
                    errorMsgs?.errors && errorMsgs.errors.holder_country_code,
                  )}
                />
              )}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="holder_country_code" />
          </Grid>
          {value.holder_country_code && value.holder_country_code.code === 'US' && (
            <Grid item md={6} xs={12}>
              <Autocomplete
                id="state-select"
                options={usRegionsWithCodes}
                classes={{
                  option: classes.option,
                }}
                autoHighlight
                getOptionLabel={(option) => option.name || ''}
                renderOption={(option) => <>{option.name}</>}
                value={value.state_code || null}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    state_code: newValue || null,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(
                      'fe_earnings_and_payouts_page:add_payout_method_dialog.fields.state_code',
                    )}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                    error={Boolean(errorMsgs?.errors && errorMsgs.errors.state_code)}
                  />
                )}
              />
              <FormErrors errorMsgs={errorMsgs} errorField="state_code" />
            </Grid>
          )}
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="address"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.address')}
              variant="outlined"
              name="address"
              type="text"
              value={value.address || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.address)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="address" />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="city_name"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.city_name')}
              variant="outlined"
              name="city_name"
              type="text"
              value={value.city_name || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.city_name)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="city_name" />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              id="postal_code"
              required
              color="secondary"
              label={t('fe_earnings_and_payouts_page:add_payout_method_dialog.fields.postal_code')}
              variant="outlined"
              name="postal_code"
              type="text"
              value={value.postal_code || ''}
              onChange={handleOnChange}
              error={Boolean(errorMsgs?.errors && errorMsgs.errors.postal_code)}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="postal_code" />
          </Grid>
        </Grid>
        <Grid container className={classes.submit}>
          <Grid item xs={12} lg={9}>
            <Typography variant="body2">
              EagleShare utilizes Hyperwallet payment services to deliver payments to you. Such
              payment services are subject to the
              {' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://pay.hyperwallet.com/hw2web/consumer/page/legalAgreement.xhtml"
              >
                Hyperwallet Terms of Service and the Hyperwallet Privacy Policy
              </a>
              .
            </Typography>
          </Grid>
          <Grid item xs={12} lg={3}>
            <div className={classes.right}>
              <Button
                color="primary"
                variant="contained"
                size="large"
                onClick={submit}
                disabled={loading}
                disableElevation
              >
                {t('fe_earnings_and_payouts_page:add_payout_method_dialog.submit')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

AddPayoutMethodDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default withTranslation(['fe_earnings_and_payouts_page'])(AddPayoutMethodDialog);
